import { WorkspaceFormFields } from '@/components/WorkspaceForm/WorkspaceForm.types';
import { useToast } from '@/composables/useToast';
import { endpointsList } from '@/helpers';
import axios from 'axios';
import { ICreateWorkspaceDto, IUpdateWorkspaceDto } from 'types';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

export function useSaveWorkspace() {
  const { createSuccessToast, createErrorToast } = useToast();
  const { t } = useI18n();

  const isLoading = ref(false);

  async function createWorkspace(
    formFields: WorkspaceFormFields,
    ownersOids: string[],
  ): Promise<string | undefined> {
    const dto: ICreateWorkspaceDto = {
      name: formFields.name,
      location: formFields.location,
      startDate: formFields.durationStart,
      endDate: formFields.durationEnd,
      owners: ownersOids,
    };
    try {
      isLoading.value = true;
      const response = await axios.post<string>(
        endpointsList.workspaces.createWorkspace(),
        dto,
      );
      createSuccessToast(t('workspaceForm.workspaceCreated'));
      return response.data;
    } catch {
      createErrorToast(t('workspaceForm.errors.errorCreatingWorkspace'));
    } finally {
      isLoading.value = false;
    }
  }

  async function updateWorkspace(
    workspaceId: string,
    formFields: WorkspaceFormFields,
  ): Promise<void> {
    if (!formFields.status) {
      throw new Error('Workspace status is required for updating workspace');
    }
    const dto: IUpdateWorkspaceDto = {
      name: formFields.name,
      location: formFields.location,
      startDate: formFields.durationStart,
      endDate: formFields.durationEnd,
      status: formFields.status,
    };
    try {
      isLoading.value = true;
      await axios.put(endpointsList.workspaces.editWorkspace(workspaceId), dto);
      createSuccessToast(t('workspaceForm.workspaceUpdated'));
    } catch {
      createErrorToast(t('workspaceForm.errors.errorUpdatingWorkspace'));
    } finally {
      isLoading.value = false;
    }
  }

  return {
    isLoading,
    createWorkspace,
    updateWorkspace,
  };
}
